import React from 'react';
import { Button } from '../components/ui/Button';
import {
  Link2,
  ShieldCheck,
  Zap,
  ArrowRight,
  Globe2,
  CheckCircle2,
  Lock,
  ExternalLink,
} from 'lucide-react';

interface PublicLandingProps {
  onEnterDashboard: () => void;
}

export const PublicLanding: React.FC<PublicLandingProps> = ({ onEnterDashboard }) => {
  return (
    <div className="min-h-screen bg-[#090d16] text-slate-100 flex flex-col justify-between selection:bg-orange-500 selection:text-white">
      {/* Top Header */}
      <header className="border-b border-slate-800/80 bg-slate-950/60 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-orange-600 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-950/40 border border-orange-400/30">
              <Link2 className="w-4 h-4 text-white" />
            </div>
            <div className="flex items-center gap-2">
              <span className="font-extrabold text-slate-100 tracking-tight text-base">BROTOTYPE</span>
              <span className="text-xs px-2 py-0.5 rounded-md bg-orange-500/15 text-orange-400 font-semibold border border-orange-500/30">
                LNK
              </span>
            </div>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={onEnterDashboard}
            rightIcon={<ArrowRight className="w-3.5 h-3.5" />}
          >
            Staff Dashboard
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-3xl w-full text-center space-y-8 animate-in fade-in duration-500">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-400 text-xs font-semibold">
            <ShieldCheck className="w-4 h-4 text-orange-400" />
            <span>Official Brototype Link Infrastructure</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.15]">
            Secure, Lightning-Fast <br />
            <span className="bg-gradient-to-r from-orange-400 via-amber-300 to-orange-500 bg-clip-text text-transparent">
              Marketing Redirection
            </span>
          </h1>

          <p className="text-slate-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            You have reached <strong className="text-slate-200 font-mono">lnk.brototype.com</strong>, the official branded short-link domain for Brototype campaigns, workshops, admissions, and social media announcements.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
            <Button
              variant="primary"
              size="lg"
              onClick={onEnterDashboard}
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              Access Marketing Dashboard
            </Button>

            <a
              href="https://www.brototype.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2.5 rounded-xl border border-slate-700 bg-slate-900/60 hover:bg-slate-800 text-slate-300 hover:text-white text-base font-medium transition-colors flex items-center gap-2"
            >
              <span>Visit Brototype.com</span>
              <ExternalLink className="w-4 h-4 text-slate-500" />
            </a>
          </div>

          {/* Infrastructure Feature Badges */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-8 text-left">
            <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 space-y-2">
              <div className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-400 flex items-center justify-center border border-orange-500/20">
                <Zap className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-slate-200">Sub-10ms Edge Redirects</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Powered by Cloudflare Workers and D1 database distributed across hundreds of global edge data centers.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 space-y-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center border border-emerald-500/20">
                <ShieldCheck className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-slate-200">Anti-Phishing Protection</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Strict domain whitelisting, circular redirect prevention, and scheme verification protects visitors.
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800/80 space-y-2">
              <div className="w-8 h-8 rounded-lg bg-sky-500/10 text-sky-400 flex items-center justify-center border border-sky-500/20">
                <Lock className="w-4 h-4" />
              </div>
              <h4 className="text-sm font-bold text-slate-200">Zero PII Privacy</h4>
              <p className="text-xs text-slate-400 leading-relaxed">
                Visitor IP addresses and intrusive fingerprints are never logged or stored in analytics.
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800/80 py-6 text-center text-xs text-slate-500">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>&copy; {new Date().getFullYear()} Brototype. All rights reserved.</span>
          <span className="font-mono text-[11px] text-slate-600">lnk.brototype.com &bull; Cloudflare Workers Native</span>
        </div>
      </footer>
    </div>
  );
};
